import { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import { useTheme, Divider, Paper, IconButton } from "@mui/material";
import { tokens } from "../../theme";

import {
	formatDateCreatedAt,
	formatCPF,
	formatBrDate,
	formatCep,
} from "../../utils/formats";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint } from "@fortawesome/free-solid-svg-icons";

import JsPDF from "jspdf";

const formatNumber = (value) => {
	return Number(value || 0).toLocaleString("pt-BR", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
};

function getDecimalPart(value) {
	const roundedValue = Number(value || 0).toFixed(2);
	const decimalStr = roundedValue.split(".")[1];
	return decimalStr ? parseInt(decimalStr, 10) : 0;
}

function numberToPortugueseText(value) {
	value = Number(value || 0);

	const units = ["", "um", "dois", "três", "quatro", "cinco", "seis", "sete", "oito", "nove"];
	const teens = [
		"dez",
		"onze",
		"doze",
		"treze",
		"catorze",
		"quinze",
		"dezesseis",
		"dezessete",
		"dezoito",
		"dezenove",
	];
	const tens = ["", "", "vinte", "trinta", "quarenta", "cinquenta", "sessenta", "setenta", "oitenta", "noventa"];
	const hundreds = ["", "cem", "duzentos", "trezentos", "quatrocentos", "quinhentos", "seiscentos", "setecentos", "oitocentos", "novecentos"];

	if (value === 0) return "ZERO REAIS";

	let integerPart = Math.floor(value);
	let result = "";
	let decimals = "";

	if (integerPart >= 1000) {
		const thousandsPart = Math.floor(integerPart / 1000);

		if (thousandsPart === 1) {
			result += "mil";
		} else if (thousandsPart < 10) {
			result += `${units[thousandsPart]} mil`;
		} else {
			result += `${thousandsPart} mil`;
		}

		integerPart %= 1000;
	}

	if (integerPart >= 100) {
		const hundredsPart = Math.floor(integerPart / 100);
		result += (result ? ", " : "") + (hundredsPart === 1 && integerPart % 100 === 0 ? "cem" : hundreds[hundredsPart]);
		integerPart %= 100;
	}

	if (integerPart >= 20) {
		const tensPart = Math.floor(integerPart / 10);
		result += (result ? " e " : "") + tens[tensPart];
		integerPart %= 10;
	}

	if (integerPart >= 10) {
		result += (result ? " e " : "") + teens[integerPart - 10];
		integerPart = 0;
	}

	if (integerPart > 0) {
		result += (result ? " e " : "") + units[integerPart];
	}

	let decimalValue = getDecimalPart(value);

	if (decimalValue >= 20) {
		const tensPart = Math.floor(decimalValue / 10);
		decimals += (decimals ? " e " : "") + tens[tensPart];
		decimalValue %= 10;
	}

	if (decimalValue >= 10) {
		decimals += (decimals ? " e " : "") + teens[decimalValue - 10];
		decimalValue = 0;
	}

	if (decimalValue > 0) {
		decimals += (decimals ? " e " : "") + units[decimalValue];
	}

	result = result.charAt(0).toUpperCase() + result.slice(1) + " REAIS";

	if (decimals) {
		result += ` E ${decimals.toUpperCase()} CENTAVOS`;
	}

	return result.toUpperCase();
}

function dateToPortugueseText(date) {
	if (!date) return "-";

	const months = [
		"janeiro",
		"fevereiro",
		"março",
		"abril",
		"maio",
		"junho",
		"julho",
		"agosto",
		"setembro",
		"outubro",
		"novembro",
		"dezembro",
	];

	const [year, month, day] = String(date).split("-").map(Number);
	const localDate = new Date(year, month - 1, day);

	return `${localDate.getDate()} de ${months[localDate.getMonth()]} de ${localDate.getFullYear()}`.toUpperCase();
}

const PromissoryNotePreview = ({ data, valor }) => {
	const payDate = data?.data?.pay_date || "";
	const createdAt = data?.data?.created_at || "";
	const user = data?.sellers?.user || {};

	const valorPorExtenso = numberToPortugueseText(valor);
	const createdAtFormatted = createdAt
		? formatDateCreatedAt(createdAt).split("-")[0]
		: "-";

	const enderecoCompleto = [
		user?.end_logradouro,
		user?.end_numero,
		user?.end_complemento,
		user?.end_bairro,
		user?.end_cep ? formatCep(Number(user.end_cep)) : "",
		user?.end_municipio,
	]
		.filter(Boolean)
		.join(", ");

	return (
		<Box sx={{ width: "100%", color: "#000" }}>
			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 5 }}>
				<Typography sx={{ fontSize: 30, fontWeight: 900, letterSpacing: "0.025em", lineHeight: 1.1 }}>
					NOTA PROMISSÓRIA
				</Typography>
				<Typography sx={{ fontSize: 18, fontWeight: 700, textAlign: "right", mt: 0.5 }}>
					Nº 01/01
				</Typography>
			</Box>

			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 3, mb: 3 }}>
				<Typography sx={{ fontSize: 18, fontWeight: 500 }}>
					Vencimento: <b>{formatBrDate(payDate)}</b>
				</Typography>
				<Typography sx={{ fontSize: 30, fontWeight: 800, whiteSpace: "nowrap" }}>
					R$ {formatNumber(valor)}
				</Typography>
			</Box>

			<Divider sx={{ borderColor: "#111", my: 3 }} />

			<Typography sx={{ fontSize: 18, lineHeight: 1.95, textAlign: "justify", mb: 4 }}>
				No dia <b>{dateToPortugueseText(payDate)}</b>, pagarei por esta única via de{" "}
				<b>NOTA PROMISSÓRIA</b> a <b>PITAYA JOIAS</b>, CNPJ <b>30.822.328/0001-54</b>,
				ou à sua ordem, a quantia de <b>{valorPorExtenso}</b> em moeda corrente deste país.
			</Typography>

			<Divider sx={{ borderColor: "#111", my: 3 }} />

			<Box sx={{ display: "grid", gap: 1.4, mb: 5 }}>
				<Typography sx={{ fontSize: 17 }}><strong>Local de pagamento:</strong> Porto Alegre – RS</Typography>
				<Typography sx={{ fontSize: 17 }}><strong>Data da emissão:</strong> {createdAtFormatted}</Typography>
				<Typography sx={{ fontSize: 17 }}><strong>Nome do emitente:</strong> {user?.nome || "-"}</Typography>
				<Typography sx={{ fontSize: 17 }}><strong>CPF:</strong> {user?.cpf ? formatCPF(user.cpf) : "-"}</Typography>
				<Typography sx={{ fontSize: 17, lineHeight: 1.7 }}><strong>Endereço:</strong> {enderecoCompleto || "-"}</Typography>
			</Box>

			<Box sx={{ pt: 10, textAlign: "center" }}>
				<Typography sx={{ fontSize: 18, letterSpacing: "0.03em" }}>
					_______________________________________________
				</Typography>
				<Typography sx={{ fontSize: 15.5, fontWeight: 700, mt: 1.2, letterSpacing: "0.02em" }}>
					ASSINATURA DO EMITENTE
				</Typography>
			</Box>
		</Box>
	);
};

const NotaPromiPage = ({ open, setOpen, data }) => {
	const theme = useTheme();
	const colors = tokens(theme.palette.mode);

	const handleClose = () => setOpen(false);

	const valor = useMemo(() => {
		return (data?.data?.kit_products || []).reduce((acc, curr) => {
			return acc + Number(curr?.quantity || 0) * Number(curr?.sell_price || 0);
		}, 0);
	}, [data]);

	const slug = data?.sellers?.user?.slug || "sem_slug";

	const generatePDF = () => {
		const payDate = data?.data?.pay_date || "";
		const createdAt = data?.data?.created_at || "";
		const user = data?.sellers?.user || {};

		const valorFormatado = formatNumber(valor);
		const valorPorExtenso = numberToPortugueseText(valor);
		const dataExtenso = dateToPortugueseText(payDate);
		const createdAtFormatted = createdAt
			? formatDateCreatedAt(createdAt).split("-")[0]
			: "-";

		const enderecoCompleto = [
			user?.end_logradouro,
			user?.end_numero,
			user?.end_complemento,
			user?.end_bairro,
			user?.end_cep ? formatCep(Number(user.end_cep)) : "",
			user?.end_municipio,
		]
			.filter(Boolean)
			.join(", ");

		const pdf = new JsPDF("portrait", "pt", "a4");
		const pageWidth = pdf.internal.pageSize.getWidth();
		const marginX = 50;
		const contentWidth = pageWidth - marginX * 2;

		let y = 70;

		// --- CABEÇALHO ---
		pdf.setFont("helvetica", "bold");
		pdf.setFontSize(22);
		pdf.setTextColor(40, 40, 40);
		pdf.text("NOTA PROMISSÓRIA", marginX, y);

		pdf.setFont("helvetica", "normal");
		pdf.setFontSize(12);
		pdf.setTextColor(120, 120, 120);
		pdf.text("Nº 01/01", pageWidth - marginX, y, { align: "right" });

		y += 25;

		// Linha separadora 1
		pdf.setDrawColor(220, 220, 220);
		pdf.setLineWidth(0.5);
		pdf.line(marginX, y, pageWidth - marginX, y);

		y += 35;

		// --- BLOCO: VENCIMENTO E VALOR ---
		pdf.setFont("helvetica", "normal");
		pdf.setFontSize(10);
		pdf.setTextColor(140, 140, 140);
		pdf.text("VENCIMENTO", marginX, y);

		pdf.setFont("helvetica", "bold");
		pdf.setFontSize(14);
		pdf.setTextColor(40, 40, 40);
		pdf.text(formatBrDate(payDate), marginX, y + 16);

		// Caixa de Valor (Agora com borda fina e elegante, sem fundo)
		const valueBoxW = 160;
		const valueBoxH = 46;
		const valueBoxX = pageWidth - marginX - valueBoxW;
		const valueBoxY = y - 16;

		pdf.setDrawColor(200, 200, 200); // Cinza suave para a borda
		pdf.setLineWidth(0.8);
		pdf.roundedRect(valueBoxX, valueBoxY, valueBoxW, valueBoxH, 4, 4, "S"); // "S" = Stroke (apenas contorno)

		pdf.setFont("helvetica", "normal");
		pdf.setFontSize(10);
		pdf.setTextColor(140, 140, 140);
		pdf.text("VALOR", valueBoxX + 16, y);

		pdf.setFont("helvetica", "bold");
		pdf.setFontSize(18);
		pdf.setTextColor(40, 40, 40);
		pdf.text(`R$ ${valorFormatado}`, valueBoxX + valueBoxW - 16, y + 18, {
			align: "right",
		});

		y += 55;

		// --- NOVO DIVIDER ---
		// Linha separadora 2 (Entre o bloco de Valor e o Texto)
		pdf.setDrawColor(220, 220, 220);
		pdf.setLineWidth(0.5);
		pdf.line(marginX, y, pageWidth - marginX, y);

		y += 35;

		// --- CORPO DO TEXTO ---
		pdf.setFont("helvetica", "normal");
		pdf.setFontSize(12);
		pdf.setTextColor(70, 70, 70);

		const bodyText =
			`No dia ${dataExtenso}, pagarei por esta única via de NOTA PROMISSÓRIA a ` +
			`empresa PITAYA JOIAS, CNPJ 30.822.328/0001-54, ou à sua ordem, a quantia de ` +
			`${valorPorExtenso} em moeda corrente deste país.`;

		const bodyLines = pdf.splitTextToSize(bodyText, contentWidth);
		pdf.text(bodyLines, marginX, y, {
			maxWidth: contentWidth,
			align: "justify",
			lineHeightFactor: 1.7,
		});

		y += bodyLines.length * 16 + 25;

		// Linha separadora 3
		pdf.setDrawColor(230, 230, 230);
		pdf.line(marginX, y, pageWidth - marginX, y);

		y += 35;

		// --- DADOS DO EMITENTE ---
		const writeModernDetail = (label, value, currentY) => {
			pdf.setFont("helvetica", "bold");
			pdf.setFontSize(10);
			pdf.setTextColor(150, 150, 150);
			pdf.text(label, marginX, currentY);

			pdf.setFont("helvetica", "normal");
			pdf.setFontSize(11);
			pdf.setTextColor(60, 60, 60);

			const splitValue = pdf.splitTextToSize(String(value || "-"), contentWidth - 140);
			pdf.text(splitValue, marginX + 140, currentY);

			return splitValue.length * 16;
		};

		y += writeModernDetail("LOCAL DE PAGAMENTO", "Porto Alegre – RS", y) + 12;
		y += writeModernDetail("DATA DA EMISSÃO", createdAtFormatted, y) + 12;
		y += writeModernDetail("NOME DO EMITENTE", user?.nome, y) + 12;
		y += writeModernDetail("CPF", user?.cpf ? formatCPF(user.cpf) : "", y) + 12;
		y += writeModernDetail("ENDEREÇO", enderecoCompleto, y) + 10;

		y += 80;

		// --- ASSINATURA ---
		const signWidth = 240;
		const signLeft = (pageWidth - signWidth) / 2;

		pdf.setDrawColor(180, 180, 180);
		pdf.setLineWidth(0.6);
		pdf.line(signLeft, y, signLeft + signWidth, y);

		y += 20;

		pdf.setFont("helvetica", "bold");
		pdf.setFontSize(10);
		pdf.setTextColor(100, 100, 100);
		pdf.text("ASSINATURA DO EMITENTE", pageWidth / 2, y, { align: "center" });

		if (user?.nome) {
			y += 14;
			pdf.setFont("helvetica", "normal");
			pdf.setFontSize(10);
			pdf.setTextColor(150, 150, 150);
			pdf.text(user.nome, pageWidth / 2, y, { align: "center" });
		}

		pdf.save(`nota_promissoria_${slug}_${valorFormatado.replace(",", "_")}.pdf`);
	};

	const modalStyle = {
		position: "absolute",
		top: "50%",
		left: "50%",
		transform: "translate(-50%, -50%)",
		width: "min(94vw, 1240px)",
		height: "92vh",
		bgcolor: theme.palette.mode === "dark" ? colors.primary[500] : "#f7f7f9",
		color: theme.palette.mode === "dark" ? colors.grey[100] : "#111",
		border: `1px solid ${theme.palette.mode === "dark" ? colors.grey[700] : colors.grey[800]}`,
		boxShadow: 24,
		borderRadius: "24px",
		p: 2,
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		overflow: "hidden",
	};

	return (
		<Modal
			open={open}
			onClose={handleClose}
			aria-labelledby="modal-nota-promissoria"
			aria-describedby="conteudo-nota-promissoria"
		>
			<Fade in={open}>
				<Box sx={modalStyle}>
					<Paper
						elevation={0}
						sx={{
							width: "100%",
							background:
								theme.palette.mode === "dark"
									? `linear-gradient(135deg, ${colors.pink[700]} 0%, ${colors.primary[400]} 100%)`
									: `linear-gradient(135deg, ${colors.pink[600]} 0%, ${colors.blueAccent[700]} 100%)`,
							padding: "18px 20px",
							marginBottom: "16px",
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							borderRadius: "18px",
							flexShrink: 0,
						}}
					>
						<Typography
							variant="h4"
							color="white"
							fontWeight="900"
							sx={{ letterSpacing: "0.02em" }}
						>
							NOTA PROMISSÓRIA
						</Typography>

						<IconButton
							aria-label="imprimir nota promissória"
							size="small"
							onClick={generatePDF}
							sx={{
								backgroundColor: "rgba(255,255,255,0.12)",
								color: "#fff",
								borderRadius: "12px",
								width: 40,
								height: 40,
								"&:hover": {
									backgroundColor: "rgba(255,255,255,0.2)",
								},
							}}
						>
							<FontAwesomeIcon icon={faPrint} />
						</IconButton>
					</Paper>

					<Box
						sx={{
							width: "100%",
							height: "100%",
							overflow: "auto",
							backgroundColor: theme.palette.mode === "dark" ? colors.primary[400] : "#ececf1",
							borderRadius: "18px",
							padding: { xs: "12px", md: "18px" },
							boxSizing: "border-box",
						}}
					>
						<Box
							sx={{
								width: "100%",
								maxWidth: "1120px",
								margin: "0 auto",
								backgroundColor: "#fff",
								border: "1px solid #d8d8de",
								boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
								padding: { xs: "28px 24px", md: "56px 72px" },
								boxSizing: "border-box",
							}}
						>
							<PromissoryNotePreview data={data} valor={valor} />
						</Box>
					</Box>
				</Box>
			</Fade>
		</Modal>
	);
};

export default NotaPromiPage;